/**
 * Web Crypto API 原生加密工具（Web 端性能优化）
 *
 * 使用浏览器原生 Web Crypto API 实现 AES-256-CBC + HMAC-SHA256。
 * 相比纯 JS 实现，性能显著更优。
 *
 * 功能：
 * - AES-256-CBC 加密解密
 * - HMAC-SHA256 签名验证
 * - 原生安全随机 IV 生成
 *
 * 运行要求：
 * - 环境必须提供 Web Crypto API
 */

// ────────── 常量 ──────────

/** IV 长度（16 字节 = 128 位） */
const IV_LENGTH = 16;

/** 密钥长度（Hex 字符数，64 字符 = 32 字节 = 256 位） */
const KEY_HEX_LENGTH = 64;

// ────────── 环境检测 ──────────

/**
 * 检测当前环境是否支持 Web Crypto API
 */
export function isWebCryptoAvailable(): boolean {
  return (
    typeof crypto !== 'undefined' &&
    typeof (crypto as { subtle?: unknown }).subtle !== 'undefined'
  );
}

// ────────── 密钥处理 ──────────

/**
 * 将 Hex 字符串转换为 ArrayBuffer
 */
function hexToArrayBuffer(hex: string): ArrayBuffer {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = Number.parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes.buffer;
}

/**
 * 将 ArrayBuffer 转换为 Hex 字符串
 */
function arrayBufferToHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * 将 Hex 密钥转换为 CryptoKey
 */
async function importKey(
  hexKey: string,
  algorithm: AesKeyGenParams | HmacImportParams,
): Promise<CryptoKey> {
  const keyBuffer = hexToArrayBuffer(hexKey);
  return await crypto.subtle.importKey('raw', keyBuffer, algorithm, false, [
    'encrypt',
    'decrypt',
  ]);
}

/**
 * 将 Hex 密钥转换为 HMAC CryptoKey
 */
async function importHmacKey(hexKey: string): Promise<CryptoKey> {
  const keyBuffer = hexToArrayBuffer(hexKey);
  return await crypto.subtle.importKey(
    'raw',
    keyBuffer,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign', 'verify'],
  );
}

// ────────── 随机 IV 生成 ──────────

/**
 * 生成随机 IV（使用 Web Crypto API）
 * @returns Uint8Array (16 字节)
 */
async function generateRandomIV(): Promise<Uint8Array> {
  return crypto.getRandomValues(new Uint8Array(IV_LENGTH));
}

// ────────── 密钥校验 ──────────

/**
 * 校验密钥格式
 * @param keyHex Hex 字符串（64 字符 = 32 字节 = 256 位）
 */
function validateKeyHex(keyHex: string): void {
  if (!keyHex || keyHex.length !== KEY_HEX_LENGTH) {
    throw new Error(
      `[Crypto-Native] 密钥格式错误：需要 ${KEY_HEX_LENGTH} 个字符的 Hex 字符串，当前长度 ${keyHex?.length || 0}`,
    );
  }
}

// ────────── AES-256-CBC 加密解密 ──────────

/**
 * AES-256-CBC 加密
 * @param plaintext 明文字符串
 * @param keyHex 密钥（Hex 字符串，64 字符）
 * @returns Base64(IV + 密文)
 */
export async function encryptAESNative(
  plaintext: string,
  keyHex: string,
): Promise<string> {
  if (!plaintext) return plaintext;
  validateKeyHex(keyHex);

  const iv = await generateRandomIV();
  const key = await importKey(keyHex, { name: 'AES-CBC', length: 256 });

  const encoder = new TextEncoder();
  const data = encoder.encode(plaintext);

  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv: iv.buffer as ArrayBuffer },
    key,
    data,
  );

  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(encrypted), iv.length);

  return btoa(String.fromCharCode(...combined));
}

/**
 * AES-256-CBC 解密
 * @param encryptedBase64 Base64(IV + 密文)
 * @param keyHex 密钥（Hex 字符串）
 * @returns 明文字符串
 */
export async function decryptAESNative(
  encryptedBase64: string,
  keyHex: string,
): Promise<string> {
  if (!encryptedBase64) return encryptedBase64;
  validateKeyHex(keyHex);

  const combined = Uint8Array.from(atob(encryptedBase64), (c) =>
    c.charCodeAt(0),
  );

  if (combined.length < IV_LENGTH + 16) {
    throw new Error('[Crypto-Native] 加密数据长度不足');
  }

  const iv = combined.slice(0, IV_LENGTH);
  const ciphertext = combined.slice(IV_LENGTH);

  const key = await importKey(keyHex, { name: 'AES-CBC', length: 256 });

  try {
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-CBC', iv },
      key,
      ciphertext,
    );

    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
  } catch (error) {
    throw new Error('[Crypto-Native] 解密结果为空，可能密钥不正确', {
      cause: error,
    });
  }
}

// ────────── HMAC-SHA256 签名 ──────────

/**
 * HMAC-SHA256 签名
 * @param data 数据
 * @param keyHex 密钥（Hex 字符串）
 * @returns 签名（Hex 字符串）
 */
export async function generateSignatureNative(
  data: string,
  keyHex: string,
): Promise<string> {
  const key = await importHmacKey(keyHex);
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);

  const signature = await crypto.subtle.sign('HMAC', key, dataBuffer);
  return arrayBufferToHex(signature);
}

/**
 * 验证 HMAC-SHA256 签名
 * @param data 数据
 * @param signature 待验证的签名（Hex 字符串）
 * @param keyHex 密钥（Hex 字符串）
 * @returns 签名是否有效
 */
export async function verifySignatureNative(
  data: string,
  signature: string,
  keyHex: string,
): Promise<boolean> {
  if (!signature) return false;
  const expected = await generateSignatureNative(data, keyHex);
  return constantTimeCompare(expected, signature);
}

/**
 * 恒定时间字符串比较，防止时序攻击
 */
function constantTimeCompare(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return result === 0;
}

// ────────── SHA-256 哈希 ──────────

/**
 * SHA-256 哈希
 * @param data 数据
 * @returns 哈希值（Hex 字符串）
 */
export async function sha256Native(data: string): Promise<string> {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hash = await crypto.subtle.digest('SHA-256', dataBuffer);
  return arrayBufferToHex(hash);
}
